import { formatMinor } from './helpers';
import {
    InvoiceActivity,
    InvoiceActivityChange,
    InvoiceActivityEditAudit,
    InvoiceActivityKind,
} from './types';

const HEADER_LABELS: Record<InvoiceActivityKind, string> = {
    'template.created': 'Created a template',
    'template.saved': 'Saved a template',
    'template.activated': 'Activated a template',
    'template.deactivated': 'Deactivated a template',
    'template.removed': 'Removed a template',
    'invoice.created': 'Created this Invoice',
    'invoice.sent': 'Marked this Invoice as sent',
    'invoice.paid': 'Marked this Invoice as paid',
    'invoice.voided': 'Voided this Invoice',
    'invoice.edited': 'Updated this Invoice',
    'invoice.signingLinkCreated': 'Created a signing link',
    'invoice.signingLinkRevoked': 'Revoked the signing link',
    'invoice.signed': 'Signed this Invoice',
};

const FALLBACK_LINES: Record<InvoiceActivityKind, string> = {
    'template.created': 'I created a template!',
    'template.saved': 'I saved a template!',
    'template.activated': 'I activated a template!',
    'template.deactivated': 'I deactivated a template!',
    'template.removed': 'I removed a template!',
    'invoice.created': 'I created this invoice!',
    'invoice.sent': 'I marked this invoice as sent!',
    'invoice.paid': 'I marked this invoice as paid!',
    'invoice.voided': 'I voided this invoice!',
    'invoice.edited': 'I updated this invoice!',
    'invoice.signingLinkCreated': 'I created a signing link!',
    'invoice.signingLinkRevoked': 'I revoked the signing link!',
    'invoice.signed': 'This invoice was signed!',
};

export const activityHeaderLabel = (
    activity: Pick<InvoiceActivity, 'kind'>,
) => HEADER_LABELS[activity.kind] || activity.kind;

export const activityFallbackLine = (kind: InvoiceActivityKind) => FALLBACK_LINES[kind] || '';

const ACTION_LABELS: Record<InvoiceActivityKind, string> = {
    'template.created': 'Template created',
    'template.saved': 'Template saved',
    'template.activated': 'Template activated',
    'template.deactivated': 'Template deactivated',
    'template.removed': 'Template removed',
    'invoice.created': 'Created',
    'invoice.sent': 'Marked as sent',
    'invoice.paid': 'Marked as paid',
    'invoice.voided': 'Voided',
    'invoice.edited': 'Edited',
    'invoice.signingLinkCreated': 'Signing link created',
    'invoice.signingLinkRevoked': 'Signing link revoked',
    'invoice.signed': 'Signed',
};

export const activityActionLabel = (
    activity: Pick<InvoiceActivity, 'kind' | 'actionLabel'>,
) => {
    const given = activity.actionLabel?.trim();
    if (given) return given;
    return ACTION_LABELS[activity.kind] || activity.kind;
};

export const activityActorName = (
    actor: Pick<InvoiceActivity['actor'], 'uid' | 'displayName'>,
) => {
    const name = actor.displayName?.trim() || '';
    const uid = actor.uid?.trim() || '';
    if (!name || name === uid || /^[A-Za-z0-9]{20,}$/.test(name)) return 'Unknown';
    return name;
};

export const activitySubject = (label: string, invoiceNumber?: string) => {
    const text = label.trim();
    if (!text || (invoiceNumber && text === invoiceNumber.trim())) return '';
    return text;
};

export const activityEditNote = (audit?: InvoiceActivityEditAudit) => {
    if (!audit) return '';
    const notes: string[] = [];
    if (audit.statusAtEdit && audit.statusAtEdit !== 'draft') {
        notes.push(`Saved while the invoice was ${audit.statusAtEdit}.`);
    }
    if (audit.signedAtEdit) notes.push('The customer had already signed.');
    return notes.join(' ');
};

export const activityChangeSentence = (previous: string, next: string) => {
    if (!previous && !next) return '';
    if (!previous) return `Set to ${next}`;
    if (!next) return `Cleared. Was ${previous}`;
    if (previous === next) return '';
    return `From ${previous} to ${next}`;
};

const parseActivityValue = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const isMinorField = (field: string) => {
    const segment = field.split(/[.[\]]/).filter(Boolean).pop() || '';
    return /Minor$/.test(segment);
};

const isMoneyLabel = (label: string) => (
    /(?:^|[\s·•|/:-])(subtotals?|totals?|rates?|amounts?|prices?|fees?)$/i.test(label.trim())
);

const presentParsed = (
    field: string,
    parsed: unknown,
    currency: string,
    locale: string,
    forceMoney = false,
): unknown => {
    if (Array.isArray(parsed)) {
        return parsed.map((item, index) => (
            presentParsed(`${field}.${index}`, item, currency, locale)
        ));
    }
    if (parsed && typeof parsed === 'object') {
        return Object.fromEntries(
            Object.entries(parsed).map(([key, child]) => [
                key,
                presentParsed(key, child, currency, locale),
            ]),
        );
    }
    if (
        typeof parsed === 'number'
        && Number.isFinite(parsed)
        && (forceMoney || isMinorField(field))
    ) {
        return formatMinor(parsed, currency, locale);
    }
    return parsed;
};

export const decodeActivityValue = (value: string) => {
    const parsed = parseActivityValue(value);
    if (parsed === null || parsed === undefined || parsed === '') return '';
    if (
        typeof parsed === 'string'
        || typeof parsed === 'number'
        || typeof parsed === 'boolean'
    ) {
        return String(parsed);
    }
    return JSON.stringify(parsed);
};

export const presentActivityValue = (
    field: string,
    value: string,
    currency = 'USD',
    locale = 'en-US',
    label = '',
) => {
    const parsed = presentParsed(
        field,
        parseActivityValue(value),
        currency,
        locale,
        isMinorField(field) || isMoneyLabel(label),
    );
    if (parsed === null || parsed === undefined || parsed === '') return '';
    if (
        typeof parsed === 'string'
        || typeof parsed === 'number'
        || typeof parsed === 'boolean'
    ) {
        return String(parsed);
    }
    return JSON.stringify(parsed);
};

export const describedActivityChanges = (
    changes: InvoiceActivityChange[],
    currency = 'USD',
    locale = 'en-US',
) => changes.flatMap((change) => {
    const previous = presentActivityValue(
        change.field,
        change.previousValue,
        currency,
        locale,
        change.label,
    );
    const next = presentActivityValue(
        change.field,
        change.newValue,
        currency,
        locale,
        change.label,
    );
    const sentence = activityChangeSentence(previous, next);
    if (!sentence) return [];
    return [{
        key: `${change.field}:${change.label}:${change.previousValue}:${change.newValue}`,
        label: (change.label || change.field).trim(),
        previous,
        next,
        sentence,
    }];
});
